import React from "react";
import SubTopHeader2 from "./menus/SubTopHeader2";

const WHATSAPP_PHONE = "254700000000"; // update (no +)
const whatsappUrl = `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(
    "Hi! I'd like info about your Product Authentication (Dynamic QR) service."
)}`;

export default function ProductAuthenticationPage() {
    // polish nav on scroll
    // JS-only smooth scroll (no hash, no history)
    const NAV_H = 72;
    function scrollToId(id) {
        const el = document.getElementById(id);
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.pageYOffset - NAV_H - 8;
        window.scrollTo({ top, behavior: "smooth" });
        // no URL changes, no history changes
    }

    // close mobile menu if open
    function closeMobileMenu() {
        const toggle = document.getElementById("auth-nav-toggle");
        if (toggle && toggle.checked) toggle.checked = false;
    }

    function onNavClick(targetId) {
        scrollToId(targetId);
        closeMobileMenu();
    }

    return (
        <>
            <SubTopHeader2 />
            <div className="auth-page">

                {/* HERO */}
                <section id="home" className="hero" aria-label="Hero">
                    <div className="container hero-grid">
                        <div className="hero-copy">
                            <h1>
                                Product Authentication <span className="accent">with Dynamic QR</span>
                            </h1>
                            <p className="lead">
                                Stop counterfeits, earn trust, and tell your product’s story. Each scan verifies authenticity,
                                records location/time, and can show dynamic pages per batch, SKU, or even per item.
                            </p>

                            <div className="row gap hero-actions">
                                <button type="button" className="btn primary" onClick={() => onNavClick("pricing")}>
                                    See Pricing
                                </button>
                                <button type="button" className="btn ghost" onClick={() => onNavClick("how")}>
                                    How it Works
                                </button>
                                <a href={whatsappUrl} className="btn whats" target="_blank" rel="noreferrer">
                                    Talk to us
                                </a>
                            </div>

                            <ul className="hero-bullets">
                                <li>Per-item or per-batch dynamic codes</li>
                                <li>Anti-copy signals & duplicate-scan alerts</li>
                                <li>Analytics: location heatmaps, time of day, device</li>
                            </ul>
                        </div>

                        {/* Pure CSS art */}
                        <div className="hero-art">

                            <div className="pkg">
                                <div className="pkg-top"></div>
                                <div className="pkg-face">
                                    <SimpleScanCard />
                                </div>
                            </div>

                            {/*  */}
                            <div className="halo"></div>
                        </div>
                    </div>
                </section>

                {/* HOW IT WORKS */}
                <section id="how" className="section" aria-label="How it Works">
                    <div className="container">
                        <h2>How it Works</h2>
                        <div className="steps">
                            <Step num="1" title="Issue Codes">
                                Create item-level or batch-level Dynamic QR codes. Print on labels, cartons, seals, or inserts.
                            </Step>
                            <Step num="2" title="Customer Scans">
                                Customers scan to see an authenticity result, product info, and your brand story or warranty.
                            </Step>
                            <Step num="3" title="Verify & Track">
                                We log time, approximate location, device type, and flags for duplicate or suspicious scans.
                            </Step>
                            <Step num="4" title="Act on Insights">
                                Identify grey markets and counterfeit clusters. Run targeted education or takedowns.
                            </Step>
                        </div>
                    </div>
                </section>

                {/* FEATURES */}
                <section id="features" className="section muted-bg" aria-label="Features">
                    <div className="container">
                        <h2>Features that Protect & Convert</h2>
                        <div className="grid features">
                            <Feature title="Authenticity Result">
                                <ShieldSVG />
                                Real-time “Genuine / Suspected / Duplicate” with signals and guidance for the customer.
                            </Feature>
                            <Feature title="Per-Item or Per-Batch">
                                <LayersSVG />
                                Create codes per unit, per batch, per region, or per distributor. Rotate destinations any time.
                            </Feature>
                            <Feature title="Scan Analytics">
                                <ChartSVG />
                                Location heatmaps, scan frequency, devices, first vs repeat scans, time-of-day charts.
                            </Feature>
                            <Feature title="Geo-Routing">
                                <GlobeSVG />
                                Send different content by country, language, distributor, or campaign.
                            </Feature>
                            <Feature title="Dynamic Destinations">
                                <LinkSVG />
                                Update landing pages without reprinting. Launch promos, warranties, how-to videos per code.
                            </Feature>
                            <Feature title="Duplicate Scan Alerts">
                                <ZapSVG />
                                Spot copycats: flag repeated scans or abnormal patterns; receive alerts for action.
                            </Feature>
                        </div>

                        <div className="cta-slab">
                            <p>Ready to pilot on a single product line?</p>
                            <a href={whatsappUrl} className="btn primary" target="_blank" rel="noreferrer">
                                Start Pilot on WhatsApp
                            </a>
                        </div>
                    </div>
                </section>

                {/* PRICING */}
                <section id="pricing" className="section" aria-label="Pricing">
                    <div className="container">
                        <h2>Pricing</h2>
                        <p className="muted max">Simple tiers for startups to national brands.</p>

                        <div className="grid pricing">
                            <div className="price">
                                <h3>Starter</h3>
                                <p className="price-num">KES 4,900 <span>/month</span></p>
                                <ul>
                                    <li>Up to 5k scans / month</li>
                                    <li>Per-batch codes</li>
                                    <li>Basic analytics</li>
                                </ul>
                                <a href={whatsappUrl} className="btn block whats" target="_blank" rel="noreferrer">
                                    Choose Starter
                                </a>
                                <p className="note">*Perfect for pilots & small runs.</p>
                            </div>

                            <div className="price popular" aria-label="Most popular">
                                <div className="badge">Most Popular</div>
                                <h3>Growth</h3>
                                <p className="price-num">KES 14,900 <span>/month</span></p>
                                <ul>
                                    <li>Up to 30k scans / month</li>
                                    <li>Per-item or per-batch</li>
                                    <li>Geo-routing + alerts</li>
                                    <li>Advanced analytics</li>
                                </ul>
                                <a href={whatsappUrl} className="btn primary block" target="_blank" rel="noreferrer">
                                    Choose Growth
                                </a>
                                <p className="note">*Best value for scaling SKUs.</p>
                            </div>

                            <div className="price">
                                <h3>Enterprise</h3>
                                <p className="price-num">Custom</p>
                                <ul>
                                    <li>Unlimited scans</li>
                                    <li>Multi-region content</li>
                                    <li>Private APIs & SSO</li>
                                    <li>Dedicated support</li>
                                </ul>
                                <a href={whatsappUrl} className="btn block" target="_blank" rel="noreferrer">
                                    Contact Sales
                                </a>
                                <p className="note">*For national brands & OEMs.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* FAQ */}
                <section id="faq" className="section" aria-label="Frequently Asked Questions">
                    <div className="container">
                        <h2>FAQ</h2>
                        <div className="faq">
                            <details>
                                <summary>Can’t someone just copy the QR?</summary>
                                <p>
                                    Simple copying triggers duplicate-scan patterns. We combine timing, device, geo signals and code design
                                    to flag suspicious scans and route customers accordingly. You also get alerts.
                                </p>
                            </details>
                            <details>
                                <summary>Do we need new printing equipment?</summary>
                                <p>
                                    No. You can print with your current label vendor or standard packaging printers. We’ll supply
                                    print-ready assets and batch exports.
                                </p>
                            </details>
                            <details>
                                <summary>What about offline products?</summary>
                                <p>
                                    QR works offline at print time—verification happens when the customer scans. If the user has no data,
                                    they can try again later; duplicate signals still help after the fact.
                                </p>
                            </details>
                            <details>
                                <summary>Can we show different pages per market?</summary>
                                <p>
                                    Yes. Use geo-routing, language detection, and distributors to customize your post-scan experience.
                                </p>
                            </details>
                        </div>
                    </div>
                </section>

                {/* Bottom CTA & Footer */}
                <section className="section bottom-cta" aria-label="Call to action">
                    <div className="container">
                        <h2>Protect your brand. Delight your customers.</h2>
                        <p className="muted max">Launch in days. Start with a pilot. Expand on your terms.</p>
                        <div className="row gap center">
                            <a href={whatsappUrl} className="btn primary" target="_blank" rel="noreferrer">
                                Start Pilot
                            </a>
                            <button type="button" className="btn ghost" onClick={() => onNavClick("how")}>
                                See How it Works
                            </button>
                        </div>
                    </div>
                </section>

                <footer className="footer">
                    <div className="container footer-row">
                        <span className="muted tiny">© {new Date().getFullYear()} QR Flex</span>
                        <button type="button" className="tiny linklike" onClick={() => onNavClick("home")}>
                            Back to top ↑
                        </button>
                    </div>
                </footer>
            </div>
        </>
    );
}

/* Small building blocks */
function Step({ num, title, children }) {
    return (
        <div className="step">
            <div className="step-num">{num}</div>
            <h3>{title}</h3>
            <p className="muted">{children}</p>
        </div>
    );
}

function Feature({ children, title }) {
    return (
        <div className="feature">
            <div className="feature-icon">{/* icon injected by children before text */}</div>
            <h3>{title}</h3>
            <p className="muted">{children}</p>
        </div>
    );
}

/* Inline SVGs */
function ShieldSVG() {
    return (
        <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true" className="svg-icon">
            <path d="M12 2l7 3v6c0 5-3.5 9-7 11-3.5-2-7-6-7-11V5l7-3z" fill="none" stroke="currentColor" strokeWidth="1.6" />
            <path d="M8.5 11.5l2.5 2.5 4.5-5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}
function LayersSVG() {
    return (
        <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true" className="svg-icon">
            <path d="M12 3l9 5-9 5-9-5 9-5z" fill="none" stroke="currentColor" strokeWidth="1.6" />
            <path d="M21 13l-9 5-9-5" fill="none" stroke="currentColor" strokeWidth="1.6" />
            <path d="M21 17l-9 5-9-5" fill="none" stroke="currentColor" strokeWidth="1.6" />
        </svg>
    );
}
function ChartSVG() {
    return (
        <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true" className="svg-icon">
            <path d="M4 19h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <rect x="6" y="10" width="2.8" height="7" rx="0.6" fill="currentColor" />
            <rect x="10.6" y="7" width="2.8" height="10" rx="0.6" fill="currentColor" />
            <rect x="15.2" y="12" width="2.8" height="5" rx="0.6" fill="currentColor" />
        </svg>
    );
}
function GlobeSVG() {
    return (
        <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true" className="svg-icon">
            <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.6" />
            <path d="M3 12h18M12 3c3 3.5 3 14.5 0 18M12 3c-3 3.5-3 14.5 0 18" fill="none" stroke="currentColor" strokeWidth="1.2" />
        </svg>
    );
}
function LinkSVG() {
    return (
        <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true" className="svg-icon">
            <path d="M10 7h-2a5 5 0 0 0 0 10h2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <path d="M14 7h2a5 5 0 0 1 0 10h-2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <path d="M8 12h8" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    );
}
function ZapSVG() {
    return (
        <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true" className="svg-icon">
            <path d="M13 2L3 14h7l-1 8 11-14h-7l0-6z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        </svg>
    );
}

/* --- In ProductAuthenticationPage.jsx --- */

/** Compact SVG card: QR + shield check + label */

/** Small, clean QR icon (no noisy modules) */
function SimpleScanCard() {
    return (
        <div className="scan-card" role="group" aria-label="Scan to verify">
            <div className="scan-card__head">SCAN TO VERIFY</div>
            <div className="scan-card__body">
                <span className="qr-emoji" aria-hidden="true">🔳</span>
                <div className="scan-card__meta">
                    <div className="scan-chip scan-chip--ok">
                        <span className="dot" aria-hidden="true"></span> Genuine
                    </div>
                    <div className="scan-hint">Point your camera at the code</div>
                </div>
            </div>
        </div>
    );
}


